import { cn } from '@/lib/cn.ts';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  /** widen for media-heavy content like the scanner */
  size?: 'md' | 'lg';
};

/** Centered modal that animates from its scrim. Esc + backdrop dismiss. */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  className,
  size = 'md',
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[900] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="Kapat"
            onClick={onClose}
            className="modal-backdrop absolute inset-0 cursor-default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />
          {/* biome-ignore lint/a11y/useSemanticElements: animated wrapper can't be a native <dialog> */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: 'spring', stiffness: 360, damping: 30 }}
            className={cn(
              'modal-card relative w-full overflow-hidden rounded-xl',
              size === 'lg' ? 'max-w-lg' : 'max-w-md',
              className,
            )}
          >
            {(title || subtitle) && (
              <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
                <div>
                  {title && <h2 className="text-base font-medium tracking-tight">{title}</h2>}
                  {subtitle && <p className="mt-0.5 text-xs text-text-muted">{subtitle}</p>}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Kapat"
                  className="-mr-1 -mt-1 rounded-md p-1.5 text-text-faint transition-colors hover:bg-surface hover:text-text"
                >
                  <X className="h-4 w-4" />
                </button>
              </header>
            )}
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
